import React, { useState, useEffect, useCallback } from 'react';
import { tenderAPI } from '../utils/api';
import { Plus, Loader2, Trash2, X, Check, Globe, Building2, Search, Radar, Play, AlertTriangle, ExternalLink, Clock } from 'lucide-react';

var TYPE_LABELS = {
  government_agency: 'Government',
  glc: 'GLC',
  plc: 'PLC',
  statutory_body: 'Statutory Body',
  government_portal: 'Gov Portal',
};
var TYPE_COLORS = {
  government_agency: 'bg-blue-100 text-blue-700',
  glc: 'bg-amber-100 text-amber-700',
  plc: 'bg-green-100 text-green-700',
  statutory_body: 'bg-purple-100 text-purple-700',
  government_portal: 'bg-slate-100 text-slate-600',
};

function CompanyList() {
  var s1 = useState([]); var companies = s1[0]; var setCompanies = s1[1];
  var s2 = useState(true); var loading = s2[0]; var setLoading = s2[1];
  var s3 = useState(false); var showAdd = s3[0]; var setShowAdd = s3[1];
  var s4 = useState(''); var searchTerm = s4[0]; var setSearchTerm = s4[1];
  var s5 = useState('all'); var filterType = s5[0]; var setFilterType = s5[1];
  var s6 = useState({}); var actionStatus = s6[0]; var setActionStatus = s6[1];
  var s7 = useState(false); var showAI = s7[0]; var setShowAI = s7[1];
  var s8 = useState(false); var bulkRunning = s8[0]; var setBulkRunning = s8[1];
  var s10 = useState(null); var scrapeJob = s10[0]; var setScrapeJob = s10[1];
  var s11 = useState(null); var schedulerInfo = s11[0]; var setSchedulerInfo = s11[1];

  // Poll for scrape job progress
  useEffect(function() {
    if (!scrapeJob || scrapeJob.status !== 'running') return;
    var interval = setInterval(async function() {
      try {
        var res = await tenderAPI.scrapeJobStatus(scrapeJob._id || scrapeJob.job_id);
        setScrapeJob(res.data);
        if (res.data.status !== 'running') {
          clearInterval(interval);
          setBulkRunning(false);
          fetchCompanies();
        }
      } catch (e) { clearInterval(interval); }
    }, 3000);
    return function() { clearInterval(interval); };
  }, [scrapeJob, fetchCompanies]);

  var fetchCompanies = useCallback(async function() {
    try {
      var res = await tenderAPI.sources();
      setCompanies(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  var fetchSchedulerStatus = useCallback(async function() {
    try {
      var res = await tenderAPI.schedulerStatus();
      setSchedulerInfo(res.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(function() { fetchCompanies(); fetchSchedulerStatus(); }, [fetchCompanies, fetchSchedulerStatus]);

  var handleRemove = async function(id, name) {
    if (!window.confirm('Remove ' + name + ' from the watch list?')) return;
    try {
      await tenderAPI.deleteSource(id);
      fetchCompanies();
    } catch (e) { console.error(e); }
  };

  var handleToggle = async function(company) {
    try {
      await tenderAPI.updateSource(company._id, { enabled: !company.enabled });
      fetchCompanies();
    } catch (e) { console.error(e); }
  };

  var handleScrape = async function(id) {
    var ns = {}; var k = Object.keys(actionStatus); for (var i = 0; i < k.length; i++) ns[k[i]] = actionStatus[k[i]];
    ns[id] = 'scraping'; setActionStatus(ns);
    try {
      var res = await tenderAPI.runSource(id);
      ns[id] = res.data.success ? ('done_' + (res.data.new || 0)) : 'error';
    } catch (e) { ns[id] = 'error'; }
    setActionStatus({...ns});
    fetchCompanies();
  };

  var handleBulkScrape = async function() {
    setBulkRunning(true);
    try {
      var res = await tenderAPI.runAll();
      setScrapeJob({ _id: res.data.job_id, status: 'running', completed_sources: 0, total_sources: companies.filter(function(c) { return c.enabled; }).length });
    } catch (e) { console.error(e); setBulkRunning(false); }
  };

  var handleToggleScheduler = async function() {
    try {
      if (schedulerInfo && schedulerInfo.running) {
        await tenderAPI.schedulerStop();
      } else {
        await tenderAPI.schedulerStart(6, 0);
      }
      fetchSchedulerStatus();
    } catch (e) { console.error(e); }
  };

  // Filter companies
  var filtered = [];
  for (var fi = 0; fi < companies.length; fi++) {
    var c = companies[fi];
    var matchSearch = !searchTerm || c.name.toLowerCase().indexOf(searchTerm.toLowerCase()) >= 0;
    var matchType = filterType === 'all' || c.type === filterType;
    if (matchSearch && matchType) filtered.push(c);
  }

  // Stats
  var totalActive = 0;
  var byType = {};
  for (var si = 0; si < companies.length; si++) {
    if (companies[si].enabled) totalActive++;
    var t = companies[si].type || 'other';
    byType[t] = (byType[t] || 0) + 1;
  }

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-primary" size={32} /></div>;

  var typeFilters = [<button key="all" onClick={function() { setFilterType('all'); }} className={'px-3 py-1.5 rounded-lg text-sm transition-colors ' + (filterType === 'all' ? 'bg-primary text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')} data-testid="filter-all">All ({companies.length})</button>];
  var typeKeys = Object.keys(byType);
  for (var tk = 0; tk < typeKeys.length; tk++) {
    var tkey = typeKeys[tk];
    var tlabel = TYPE_LABELS[tkey] || tkey;
    typeFilters.push(
      <button key={tkey} onClick={function(k) { return function() { setFilterType(k); }; }(tkey)} className={'px-3 py-1.5 rounded-lg text-sm transition-colors ' + (filterType === tkey ? 'bg-primary text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')} data-testid={'filter-' + tkey}>{tlabel} ({byType[tkey]})</button>
    );
  }

  var companyRows = [];
  for (var ri = 0; ri < filtered.length; ri++) {
    var co = filtered[ri];
    var st = actionStatus[co._id] || '';
    var isScraping = st === 'scraping';
    var typeColor = TYPE_COLORS[co.type] || 'bg-slate-100 text-slate-600';
    var hasError = co.consecutive_errors >= 3;
    companyRows.push(
      <CompanyRow key={co._id} company={co} typeColor={typeColor} hasError={hasError} st={st} isScraping={isScraping} onToggle={handleToggle} onScrape={handleScrape} onRemove={handleRemove} />
    );
  }

  return (
    <div className="p-6 space-y-5" data-testid="company-list-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Companies to Scout</h1>
          <p className="text-sm text-slate-500">{totalActive} active out of {companies.length} organisations being monitored</p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleToggleScheduler} className={'px-3 py-2 border rounded-lg text-sm flex items-center gap-2 transition-colors ' + (schedulerInfo && schedulerInfo.running ? 'border-green-300 bg-green-50 text-green-700' : 'border-slate-200 text-slate-500 hover:bg-slate-50')} data-testid="scheduler-toggle-btn" title={schedulerInfo && schedulerInfo.running ? 'Scheduler active - click to stop' : 'Scheduler stopped - click to start'}>
            <Clock size={16} />
            {schedulerInfo && schedulerInfo.running ? 'Auto: ON' : 'Auto: OFF'}
          </button>
          <button onClick={handleBulkScrape} disabled={bulkRunning} className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 text-sm flex items-center gap-2 disabled:opacity-50" data-testid="scrape-all-btn">
            {bulkRunning ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
            {bulkRunning ? 'Scraping...' : 'Scrape All'}
          </button>
          <button onClick={function() { setShowAI(true); }} className="px-4 py-2 border border-primary text-primary rounded-lg hover:bg-primary/5 text-sm flex items-center gap-2" data-testid="ai-suggest-btn">
            <Radar size={16} /> AI Suggest
          </button>
          <button onClick={function() { setShowAdd(true); }} className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 text-sm flex items-center gap-2" data-testid="add-company-btn">
            <Plus size={16} /> Add Company
          </button>
        </div>
      </div>

      {scrapeJob && scrapeJob.status === 'running' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center gap-3" data-testid="scrape-progress">
          <Loader2 size={16} className="animate-spin text-blue-600" />
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-800">Scraping in progress...</p>
            <div className="flex items-center gap-2 mt-1">
              <div className="flex-1 h-1.5 bg-blue-100 rounded-full overflow-hidden">
                <div className="h-full bg-blue-600 rounded-full transition-all" style={{ width: (scrapeJob.total_sources ? (scrapeJob.completed_sources / scrapeJob.total_sources * 100) : 0) + '%' }} />
              </div>
              <span className="text-xs text-blue-600">{scrapeJob.completed_sources || 0}/{scrapeJob.total_sources || '?'} sources</span>
            </div>
          </div>
        </div>
      )}

      {scrapeJob && scrapeJob.status === 'completed' && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex items-center justify-between" data-testid="scrape-complete">
          <div className="flex items-center gap-2">
            <Check size={16} className="text-green-600" />
            <p className="text-sm text-green-800">Scrape complete: <strong>{scrapeJob.total_new || 0}</strong> new tenders found from <strong>{scrapeJob.completed_sources || 0}</strong> sources</p>
          </div>
          <button onClick={function() { setScrapeJob(null); }} className="text-green-500 hover:text-green-700"><X size={16} /></button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" value={searchTerm} onChange={function(e) { setSearchTerm(e.target.value); }} placeholder="Search companies..." className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="company-search" />
        </div>
        <div className="flex gap-2 flex-wrap">{typeFilters}</div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 w-10"></th>
              <th className="py-3 px-4 text-left text-xs font-medium text-slate-500">Organisation</th>
              <th className="py-3 px-4 text-left text-xs font-medium text-slate-500">Type</th>
              <th className="py-3 px-4 text-left text-xs font-medium text-slate-500">Tender Page</th>
              <th className="py-3 px-4 text-left text-xs font-medium text-slate-500">Last Scraped</th>
              <th className="py-3 px-4 text-left text-xs font-medium text-slate-500">Tenders Found</th>
              <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 w-40">Actions</th>
            </tr>
          </thead>
          <tbody>
            {companyRows.length > 0 ? companyRows : (
              <tr><td colSpan={7} className="py-12 text-center text-sm text-slate-400">No companies added yet. Click "Add Company" or "AI Suggest" to get started.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showAdd && <AddCompanyModal onClose={function() { setShowAdd(false); }} onSuccess={function() { setShowAdd(false); fetchCompanies(); }} />}
      {showAI && <AISuggestModal onClose={function() { setShowAI(false); }} onSuccess={function() { setShowAI(false); fetchCompanies(); }} />}
    </div>
  );
}

function CompanyRow({ company, typeColor, hasError, st, isScraping, onToggle, onScrape, onRemove }) {
  var co = company;
  var tenderUrl = co.search_url || co.url;
  var shortUrl = tenderUrl ? tenderUrl.replace(/^https?:\/\//, '').substring(0, 45) : '-';
  if (shortUrl.length >= 45) shortUrl += '...';
  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50" data-testid={'company-row-' + co._id}>
      <td className="py-3 px-4">
        <button onClick={function() { onToggle(co); }} className={'w-5 h-5 rounded border flex items-center justify-center ' + (co.enabled ? 'bg-primary border-primary' : 'border-slate-300')} data-testid={'toggle-' + co._id}>
          {co.enabled && <Check size={12} className="text-white" />}
        </button>
      </td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-slate-900">{co.name}</p>
          {hasError && <span className="text-xs text-red-500 flex items-center gap-0.5"><AlertTriangle size={10} /> errors</span>}
        </div>
        {co.notes && <p className="text-xs text-slate-400 mt-0.5">{co.notes}</p>}
      </td>
      <td className="py-3 px-4"><span className={'text-xs px-2 py-1 rounded ' + typeColor}>{TYPE_LABELS[co.type] || co.type}</span></td>
      <td className="py-3 px-4">
        {tenderUrl ? <a href={tenderUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1"><Globe size={12} /> {shortUrl}</a> : <span className="text-xs text-slate-400">-</span>}
      </td>
      <td className="py-3 px-4 text-xs text-slate-500">{co.last_scraped_at ? new Date(co.last_scraped_at).toLocaleDateString() : 'Never'}</td>
      <td className="py-3 px-4 text-sm text-slate-600">{co.last_result_count || 0}</td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-1">
          <button onClick={function() { onScrape(co._id); }} disabled={isScraping} className="px-2.5 py-1 text-xs border border-slate-200 rounded hover:bg-slate-50 flex items-center gap-1 disabled:opacity-50" data-testid={'scrape-' + co._id}>
            {isScraping ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />} Scrape
          </button>
          <button onClick={function() { onRemove(co._id, co.name); }} className="p-1 text-slate-400 hover:text-red-500" data-testid={'remove-' + co._id}><Trash2 size={14} /></button>
        </div>
        {st && st.startsWith('done') && <p className="text-xs text-green-600 mt-1">{st.split('_')[1]} new tenders</p>}
        {st === 'error' && <p className="text-xs text-red-500 mt-1">Scrape failed</p>}
      </td>
    </tr>
  );
}

function AddCompanyModal({ onClose, onSuccess }) {
  var s1 = useState(''); var name = s1[0]; var setName = s1[1];
  var s2 = useState(''); var url = s2[0]; var setUrl = s2[1];
  var s3 = useState(''); var tenderPage = s3[0]; var setTenderPage = s3[1];
  var s4 = useState('glc'); var type = s4[0]; var setType = s4[1];
  var s5 = useState(''); var notes = s5[0]; var setNotes = s5[1];
  var s6 = useState(false); var saving = s6[0]; var setSaving = s6[1];
  var s7 = useState(null); var error = s7[0]; var setError = s7[1];
  var s8 = useState(false); var bulk = s8[0]; var setBulk = s8[1];
  var s9 = useState(''); var bulkText = s9[0]; var setBulkText = s9[1];

  var handleSave = async function() {
    setSaving(true); setError(null);
    try {
      if (bulk) {
        var lines = bulkText.split('\n').filter(function(l) { return l.trim(); });
        var toAdd = [];
        for (var i = 0; i < lines.length; i++) {
          var parts = lines[i].split('|').map(function(p) { return p.trim(); });
          if (parts.length >= 2) {
            toAdd.push({ name: parts[0], url: parts[1], search_url: parts[2] || parts[1], type: parts[3] || 'glc', notes: parts[4] || '' });
          }
        }
        if (toAdd.length === 0) { setError('No valid entries. Format: Name | Website | Tender Page | Type'); setSaving(false); return; }
        await tenderAPI.batchAddSources(toAdd);
      } else {
        if (!name || !url) { setError('Name and website are required'); setSaving(false); return; }
        await tenderAPI.createSource({ name: name, url: url, search_url: tenderPage || url, type: type, notes: notes });
      }
      onSuccess();
    } catch (e) { setError(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="add-company-modal">
      <div className="bg-white rounded-lg max-w-lg w-full p-6" onClick={function(e) { e.stopPropagation(); }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">Add Company</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
        </div>

        <div className="flex gap-2 mb-4">
          <button onClick={function() { setBulk(false); }} className={'px-3 py-1.5 rounded-lg text-sm ' + (!bulk ? 'bg-primary text-white' : 'bg-slate-100 text-slate-600')}>Single</button>
          <button onClick={function() { setBulk(true); }} className={'px-3 py-1.5 rounded-lg text-sm ' + (bulk ? 'bg-primary text-white' : 'bg-slate-100 text-slate-600')}>Bulk Add</button>
        </div>

        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

        {bulk ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Enter one company per line. Format: <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">Name | Website | Tender Page URL | Type</code></p>
            <textarea value={bulkText} onChange={function(e) { setBulkText(e.target.value); }} rows={8} placeholder={"KWAP | https://www.kwap.gov.my | https://www.kwap.gov.my/corporate/notice-of-tender/ | government_agency\nPetronas | https://www.petronas.com | https://www.petronas.com/doing-business-with-us | glc"} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" data-testid="bulk-input" />
            <p className="text-xs text-slate-400">Types: government_agency, glc, plc, statutory_body</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Company Name *</label><input value={name} onChange={function(e) { setName(e.target.value); }} placeholder="e.g. Petronas" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="company-name-input" /></div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Website *</label><input value={url} onChange={function(e) { setUrl(e.target.value); }} placeholder="https://www.petronas.com" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="company-url-input" /></div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Tender/Procurement Page URL</label><input value={tenderPage} onChange={function(e) { setTenderPage(e.target.value); }} placeholder="https://www.petronas.com/doing-business-with-us" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="company-tender-url-input" /><p className="text-xs text-slate-400 mt-1">If blank, the main website URL will be used</p></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                <select value={type} onChange={function(e) { setType(e.target.value); }} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="company-type-select">
                  <option value="government_agency">Government Agency</option><option value="glc">GLC</option><option value="plc">PLC</option><option value="statutory_body">Statutory Body</option>
                </select>
              </div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Notes</label><input value={notes} onChange={function(e) { setNotes(e.target.value); }} placeholder="Optional description" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></div>
            </div>
          </div>
        )}

        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 py-2 border border-slate-300 rounded-lg text-sm">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="flex-1 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="save-company-btn">{saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} {bulk ? 'Add All' : 'Add Company'}</button>
        </div>
      </div>
    </div>
  );
}

function AISuggestModal({ onClose, onSuccess }) {
  var s1 = useState(false); var loading = s1[0]; var setLoading = s1[1];
  var s2 = useState([]); var suggestions = s2[0]; var setSuggestions = s2[1];
  var s3 = useState({}); var selected = s3[0]; var setSelected = s3[1];
  var s4 = useState(false); var saving = s4[0]; var setSaving = s4[1];
  var s5 = useState(null); var error = s5[0]; var setError = s5[1];

  var handleDiscover = async function() {
    setLoading(true); setError(null);
    try {
      var res = await tenderAPI.aiDiscoverSources();
      var sugs = res.data.suggestions || [];
      setSuggestions(sugs);
      var sel = {};
      for (var i = 0; i < sugs.length; i++) sel[i] = true;
      setSelected(sel);
    } catch (e) { setError('AI discovery failed. Try again.'); }
    finally { setLoading(false); }
  };

  var toggleSelect = function(idx) {
    var ns = {}; var k = Object.keys(selected);
    for (var i = 0; i < k.length; i++) ns[k[i]] = true;
    if (ns[idx]) delete ns[idx]; else ns[idx] = true;
    setSelected(ns);
  };

  var handleAdd = async function() {
    var toAdd = [];
    for (var i = 0; i < suggestions.length; i++) { if (selected[i]) toAdd.push(suggestions[i]); }
    if (toAdd.length === 0) return;
    setSaving(true);
    try { await tenderAPI.batchAddSources(toAdd); onSuccess(); }
    catch (e) { setError('Failed to add'); }
    finally { setSaving(false); }
  };

  var selCount = Object.keys(selected).length;
  var sugItems = [];
  for (var i = 0; i < suggestions.length; i++) {
    var s = suggestions[i];
    var isSel = !!selected[i];
    var typeColor = TYPE_COLORS[s.type] || 'bg-slate-100 text-slate-600';
    sugItems.push(
      <div key={i} onClick={function(idx) { return function() { toggleSelect(idx); }; }(i)} className={'p-3 rounded-lg border cursor-pointer transition-colors ' + (isSel ? 'border-primary bg-primary/5' : 'border-slate-200 hover:border-slate-300')}>
        <div className="flex items-start gap-3">
          <div className={'w-4 h-4 mt-1 rounded border flex items-center justify-center flex-shrink-0 ' + (isSel ? 'bg-primary border-primary' : 'border-slate-300')}>{isSel && <Check size={12} className="text-white" />}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-slate-900">{s.name}</p>
              <span className={'text-xs px-1.5 py-0.5 rounded ' + typeColor}>{TYPE_LABELS[s.type] || s.type}</span>
            </div>
            {s.search_url && <p className="text-xs text-primary truncate">{s.search_url}</p>}
            {s.notes && <p className="text-xs text-slate-400">{s.notes}</p>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="ai-suggest-modal">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={function(e) { e.stopPropagation(); }}>
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
          <div><h2 className="text-lg font-semibold">AI Suggest Companies</h2><p className="text-sm text-slate-500">Claude finds Malaysian organisations with public procurement pages</p></div>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          {error && <p className="text-sm text-red-600">{error}</p>}
          {suggestions.length === 0 && !loading && (
            <div className="text-center py-8">
              <Building2 size={40} className="text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-600 mb-4">AI will suggest government agencies, GLCs, PLCs, and statutory bodies that publish tender notices on their websites.</p>
              <button onClick={handleDiscover} className="px-6 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2 mx-auto" data-testid="ai-discover-btn"><Radar size={16} /> Find Companies</button>
            </div>
          )}
          {loading && <div className="text-center py-8"><Loader2 size={32} className="animate-spin text-primary mx-auto mb-3" /><p className="text-sm text-slate-500">Discovering organisations...</p></div>}
          {suggestions.length > 0 && !loading && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">Found {suggestions.length} organisations. {selCount} selected.</p>
              {sugItems}
              <button onClick={handleAdd} disabled={saving || selCount === 0} className="w-full py-2.5 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="add-suggested-btn">{saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add {selCount} Companies</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CompanyList;
